import type { Dir, FoodKind } from '../engine/types.js';
import type { DeltaMsg, KeyframeMsg } from './binary.js';

export interface MirrorSnake {
  slot: number;
  alive: boolean;
  team: number;
  dir: Dir;
  score: number;
  kills: number;
  /** Active power-up effects (EFFECT_* bitmask). */
  effects: number;
  /** Lives left (0-3; 3 also means unlimited). */
  lives: number;
  body: number[];
  /** Body as it was before the latest tick, used for render interpolation. */
  prevBody: number[];
}

/**
 * Client-side reconstruction of authoritative state from keyframe + deltas.
 * Kept free of DOM code so it is unit-tested alongside the engine.
 */
export class StateMirror {
  tick = -1;
  phase = 0;
  width = 0;
  height = 0;
  snakes: MirrorSnake[] = [];
  food = new Map<number, FoodKind>();
  obstacles = new Set<number>();
  /** Set when a delta does not follow the previous tick; the client should request a resync. */
  desynced = false;
  /** Scores arrive divided by the match's point scale; set from GameConfig.rules.pointScale. */
  scoreScale = 1;

  applyKeyframe(k: KeyframeMsg): void {
    this.tick = k.tick;
    this.phase = k.phase;
    this.width = k.width;
    this.height = k.height;
    this.snakes = k.players.map((p) => ({ ...p, score: p.score * this.scoreScale, body: [...p.body], prevBody: [...p.body] }));
    this.food = new Map(k.food.map((f) => [f.cell, f.kind]));
    this.obstacles = new Set(k.obstacles);
    this.desynced = false;
  }

  applyDelta(d: DeltaMsg): void {
    if (this.tick < 0) return;
    if (d.tick !== this.tick + 1) {
      this.desynced = true;
      return;
    }
    this.tick = d.tick;
    this.phase = d.phase;
    for (const u of d.players) {
      const s = this.snakes.find((x) => x.slot === u.slot);
      if (!s) continue;
      s.prevBody = s.body.slice();
      s.dir = u.dir;
      s.score = u.score * this.scoreScale;
      s.kills = u.kills;
      s.effects = u.effects;
      s.lives = u.lives;
      if (s.alive && !u.alive) {
        s.alive = false;
        s.body = [];
        continue;
      }
      if (u.head !== null) {
        s.body.unshift(u.head);
        if (u.tailPopped) s.body.pop();
      }
    }
    for (const c of d.foodRemoved) this.food.delete(c);
    for (const f of d.foodAdded) this.food.set(f.cell, f.kind);
    for (const c of d.obstaclesAdded) this.obstacles.add(c);
    for (const x of d.extraMoves) {
      const s = this.snakes.find((y) => y.slot === x.slot);
      if (!s?.alive) continue;
      s.body.unshift(x.head);
      if (x.tailPopped) s.body.pop();
    }
    for (const slot of d.trimmed) {
      const s = this.snakes.find((y) => y.slot === slot);
      if (s?.alive && s.body.length > 1) s.body.pop();
    }
    for (const x of d.shocked) {
      const s = this.snakes.find((y) => y.slot === x.slot);
      if (s?.alive && x.length > 0 && x.length < s.body.length) s.body.length = x.length;
    }
    for (const r of d.respawned) {
      const s = this.snakes.find((x) => x.slot === r.slot);
      if (!s) continue;
      s.alive = true;
      s.dir = r.dir;
      s.body = [...r.body];
      s.prevBody = [...r.body];
    }
  }
}
